import { useReducer, useEffect, useRef } from "react";
import { useLocalStorageSync } from "./useLocalStorageSync";
import { v4 as uuidv4 } from "uuid";
import { lang } from "../lang";

// Reducer for to-do list state management
const todoReducer = (state, action) => {
  switch (action.type) {
    case "SET_LISTS":
      if (process.env.NODE_ENV === "development") {
        console.log("Setting todo lists:", action.lists);
      }
      return action.lists;
    case "ADD_LIST":
      if (process.env.NODE_ENV === "development") {
        console.log("Dispatching ADD_LIST");
      }
      return [
        ...state,
        {
          id: uuidv4(),
          name: lang.todoList.newList,
          items: [],
          type: "todo",
        },
      ];
    case "ADD_BOOK_LIST":
      if (process.env.NODE_ENV === "development") {
        console.log("Dispatching ADD_BOOK_LIST");
      }
      return [
        ...state,
        {
          id: uuidv4(),
          name: lang.todoList.newList,
          items: [],
          type: "book",
        },
      ];
    case "UPDATE_LIST":
      if (process.env.NODE_ENV === "development") {
        console.log("Updating list:", action.id, "updates:", action.updates);
      }
      return state.map((list) =>
        list.id === action.id ? { ...list, ...action.updates } : list
      );
    case "DELETE_LIST":
      if (process.env.NODE_ENV === "development") {
        console.log("Deleting list:", action.id);
      }
      return state.filter((list) => list.id !== action.id);
    default:
      if (process.env.NODE_ENV === "development") {
        console.log("Unknown action:", action);
      }
      return state;
  }
};

// Custom hook for to-do list state
export function useTodoLists() {
  const { getItem, setItem } = useLocalStorageSync(["todoLists"]);
  const [todoLists, dispatch] = useReducer(todoReducer, []);
  const hasLoadedRef = useRef(false);

  // Load to-do lists from local storage only once on mount
  useEffect(() => {
    if (hasLoadedRef.current) return; // Prevent reloading
    const storedLists = getItem("todoLists", []);
    if (process.env.NODE_ENV === "development") {
      console.log("Loaded todo lists:", storedLists);
    }
    dispatch({ type: "SET_LISTS", lists: storedLists });
    hasLoadedRef.current = true;
  }, [getItem]);

  // Sync to-do lists to local storage when changed
  useEffect(() => {
    if (hasLoadedRef.current) {
      if (process.env.NODE_ENV === "development") {
        console.log("Saving todoLists to storage:", todoLists);
      }
      setItem("todoLists", todoLists);
    }
  }, [todoLists, setItem]);

  return [todoLists, dispatch];
}
